import { DemoNetworkCheckCard } from './DemoNetworkCheckCard'
import { DemoNetworkDiagnosticsCard } from './DemoNetworkDiagnosticsCard'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd'
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import {
  approveTeacherDemo,
  getTeacherDemoAttempt,
  getTeacherDemoAttempts,
  rejectTeacherDemo,
} from '~/services/teacher-application'
import {
  ITeacherDemoAttempt,
  ITeacherDemoDetail,
  TeacherDemoStatus,
} from '~/types/teacher-application'

const { TextArea } = Input

const Replay = styled.video`
  display: block;
  width: 100%;
  max-height: 52vh;
  background: #000;
  border-radius: 8px;
`

const statusLabels: Record<TeacherDemoStatus, { color: string; label: string }> = {
  READY: { color: 'default', label: '待开始' },
  IN_PROGRESS: { color: 'processing', label: '进行中' },
  PROCESSING: { color: 'cyan', label: '录像处理中' },
  AWAITING_SUBMISSION: { color: 'blue', label: '待老师提交' },
  UNDER_REVIEW: { color: 'orange', label: '待审核' },
  APPROVED: { color: 'green', label: '已通过' },
  REJECTED: { color: 'red', label: '最终拒绝' },
  TECHNICAL_FAILED: { color: 'volcano', label: '技术失败' },
}

const renderStatus = (status: string) => {
  const normalized = status.toUpperCase() as TeacherDemoStatus
  const config = statusLabels[normalized] || { color: 'default', label: status }
  return <Tag color={config.color}>{config.label}</Tag>
}

const formatTime = (value?: number) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'

const formatDuration = (seconds?: number) => {
  if (seconds == null) return '-'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

interface Props {
  admin: boolean
}

const DemoReviewPanel = ({ admin }: Props) => {
  const [loading, setLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [attempts, setAttempts] = useState<ITeacherDemoAttempt[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [status, setStatus] = useState('UNDER_REVIEW')
  const [selected, setSelected] = useState<ITeacherDemoAttempt>()
  const [detail, setDetail] = useState<ITeacherDemoDetail>()
  const [replayIndex, setReplayIndex] = useState(0)
  const [replayError, setReplayError] = useState(false)
  const detailRequest = useRef(0)
  const listRequest = useRef(0)
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>()
  const [reviewNotes, setReviewNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchAttempts = useCallback(() => {
    const request = ++listRequest.current
    setLoading(true)
    getTeacherDemoAttempts({ page, size, status })
      .then(({ data }) => {
        if (request !== listRequest.current) return
        setAttempts(data.content)
        setTotal(data.total || 0)
      })
      .catch((error) => message.error(error?.message || 'Demo 审核列表加载失败'))
      .finally(() => { if (request === listRequest.current) setLoading(false) })
  }, [page, size, status])

  useEffect(() => {
    fetchAttempts()
  }, [fetchAttempts])

  const openDetail = useCallback((attempt: ITeacherDemoAttempt) => {
    const request = ++detailRequest.current
    setSelected(attempt)
    setDetail(undefined)
    setReplayIndex(0)
    setReplayError(false)
    setDetailLoading(true)
    getTeacherDemoAttempt(attempt.attemptId)
      .then(({ data }) => { if (request === detailRequest.current) { setDetail(data); setSelected(data.attempt) } })
      .catch((error) => message.error(error?.message || 'Demo 详情加载失败'))
      .finally(() => { if (request === detailRequest.current) setDetailLoading(false) })
  }, [])

  const openReview = useCallback((action: 'approve' | 'reject') => {
    setReviewNotes('')
    setReviewAction(action)
  }, [])

  const submitReview = useCallback(async () => {
    if (!selected || !reviewAction) return
    if (reviewAction === 'reject' && !reviewNotes.trim()) {
      message.error('最终拒绝必须填写原因')
      return
    }
    setSubmitting(true)
    try {
      if (reviewAction === 'approve') {
        await approveTeacherDemo(selected.attemptId, { notes: reviewNotes.trim() || undefined })
        message.success('Demo 审核已通过，老师完成上岗任务后自动获得接课资格')
      } else {
        await rejectTeacherDemo(selected.attemptId, { notes: reviewNotes.trim() })
        message.success('Demo 已最终拒绝')
      }
      setReviewAction(undefined)
      setSelected(undefined)
      setDetail(undefined)
      fetchAttempts()
    } catch (error: unknown) {
      message.error((error as Error)?.message || '审核操作失败')
    } finally {
      setSubmitting(false)
    }
  }, [fetchAttempts, reviewAction, reviewNotes, selected])

  const columns = useMemo<TableProps<ITeacherDemoAttempt>['columns']>(() => [
    {
      title: '老师',
      key: 'teacher',
      fixed: 'left',
      width: 190,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{record.teacherName}</Typography.Text>
          <Typography.Text type="secondary">ID: {record.teacherId}</Typography.Text>
        </Space>
      ),
    },
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: renderStatus },
    { title: '脚本版本', dataIndex: 'scriptVersion', key: 'scriptVersion', width: 210 },
    { title: '录像状态', dataIndex: 'recordingStatus', key: 'recordingStatus', width: 130, render: (value) => value || '-' },
    { title: '课堂时长', dataIndex: 'durationSeconds', key: 'durationSeconds', width: 110, render: formatDuration },
    { title: '提交时间', dataIndex: 'submittedAt', key: 'submittedAt', width: 180, render: formatTime },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
            详情
          </Button>
        </Space>
      ),
    },
  ], [openDetail])

  const files = detail?.videoUrls ?? []
  const playableFiles = files.filter(url => /\.mp4(?:[?#]|$)/i.test(url))
  const playableUrl = playableFiles[replayIndex] || playableFiles[0] || ''
  const fallbackUrl = files[0] || ''
  const canReview = admin && !detailLoading && detail?.attempt.status === 'UNDER_REVIEW'
  const recordingExpired = Boolean(detail?.recordingExpiresAt && detail.recordingExpiresAt <= Date.now())
  const canApprove = canReview && files.length > 0 && !recordingExpired

  return (
    <>
      <Alert
        className="demo-summary"
        type="info"
        showIcon
        message="Demo 课堂为第二步最终审核"
        description="录像包含摄像头、麦克风、课件/白板及屏幕分享，仅管理员可见并保留 90 天。当前不提供重新审核。"
      />
      <div className="top">
        <Space>
          <Select
            value={status}
            onChange={(value) => { setStatus(value); setPage(1) }}
            style={{ width: 150 }}
            options={[
              { value: 'UNDER_REVIEW', label: '待审核' },
              { value: 'AWAITING_SUBMISSION', label: '待老师提交' },
              { value: 'APPROVED', label: '已通过' },
              { value: 'REJECTED', label: '最终拒绝' },
              { value: 'PROCESSING', label: '录像处理中' },
              { value: 'TECHNICAL_FAILED', label: '技术失败' },
              { value: 'ALL', label: '全部状态' },
            ]}
          />
          <Button type="primary" loading={loading} onClick={fetchAttempts}>查询</Button>
        </Space>
      </div>
      <div className="table">
        <Table
          rowKey="attemptId"
          loading={loading}
          columns={columns}
          dataSource={attempts}
          scroll={{ x: 1200, y: 500 }}
          pagination={{
            total,
            pageSize: size,
            current: page,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (value) => `共 ${value} 条`,
            onChange: (nextPage, nextSize) => { setPage(nextPage); setSize(nextSize) },
          }}
        />
      </div>

      <Modal
        title="Demo 课堂审核详情"
        open={Boolean(selected) && !reviewAction}
        onCancel={() => { ++detailRequest.current; setSelected(undefined); setDetail(undefined) }}
        footer={canReview ? <Space>
          <Button danger icon={<CloseOutlined />} onClick={() => openReview('reject')}>最终拒绝</Button>
          <Button type="primary" icon={<CheckOutlined />} disabled={!canApprove}
            title={!canApprove ? '需要有效的课堂录像才能通过审核' : undefined}
            onClick={() => openReview('approve')}>通过并启用老师</Button>
        </Space> : null}
        width={920}
        loading={detailLoading}
        destroyOnClose
      >
        {selected && detail && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="老师">{selected.teacherName}</Descriptions.Item>
              <Descriptions.Item label="审核状态">{renderStatus(selected.status)}</Descriptions.Item>
              <Descriptions.Item label="脚本版本">{detail.scriptVersion}</Descriptions.Item>
              <Descriptions.Item label="课堂时长">{formatDuration(detail.durationSeconds)}</Descriptions.Item>
              <Descriptions.Item label="录制同意时间">{formatTime(detail.consentAcceptedAt)}</Descriptions.Item>
              <Descriptions.Item label="录像保留至">{formatTime(detail.recordingExpiresAt)}</Descriptions.Item>
              <Descriptions.Item label="开始时间">{formatTime(detail.startedAt)}</Descriptions.Item>
              <Descriptions.Item label="提交时间">{formatTime(selected.submittedAt)}</Descriptions.Item>
              <Descriptions.Item label="审核时间">{formatTime(selected.reviewedAt)}</Descriptions.Item>
              <Descriptions.Item label="审核人">{detail.reviewedBy || '-'}</Descriptions.Item>
              {detail.reviewNotes && <Descriptions.Item label="审核意见" span={2}>{detail.reviewNotes}</Descriptions.Item>}
              {detail.technicalFailureReason && <Descriptions.Item label="技术错误" span={2}>{detail.technicalFailureReason}</Descriptions.Item>}
            </Descriptions>

            {Boolean(detail.technicalHistory?.length) && <Card title="技术故障记录（不计为教学审核拒绝）" size="small">
              <Timeline items={detail.technicalHistory!.map((failure, index) => ({
                key: `${failure.generation}-${failure.failedAt}-${index}`,
                children: <><Typography.Text>{formatTime(failure.failedAt)} · {failure.reason}</Typography.Text>
                  <div><Typography.Text type="secondary">故障录像保留至：{formatTime(failure.expiresAt)}</Typography.Text></div></>,
              }))} />
            </Card>}

            <DemoNetworkCheckCard check={detail.networkCheck} />
            <DemoNetworkDiagnosticsCard batches={detail.networkDiagnostics} startedAt={detail.startedAt} />

            <Card title="课堂录像" size="small">
              {playableUrl ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {playableFiles.length > 1 && <Select
                    aria-label="录像片段"
                    value={replayIndex}
                    onChange={value => { setReplayIndex(value); setReplayError(false) }}
                    options={playableFiles.map((_, index) => ({ value: index, label: `录像片段 ${index + 1} / ${playableFiles.length}` }))}
                    style={{ minWidth: 180 }}
                  />}
                  <Replay key={playableUrl} controls preload="metadata" src={playableUrl} onError={() => setReplayError(true)} />
                  {replayError && <Alert type="warning" showIcon message="录像暂时无法播放。链接可能已过期，请刷新后重试。"
                    action={<Button size="small" onClick={() => openDetail(selected)}>刷新录像</Button>} />}
                </Space>
              ) : fallbackUrl ? (
                <Typography.Link className="demo-video-link" href={fallbackUrl} target="_blank" rel="noreferrer">
                  在新窗口查看录像
                </Typography.Link>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可用录像（处理中或已到保留期限）" />
              )}
            </Card>

            <Card title="试听脚本时间线" size="small">
              {detail.steps.length ? (
                <Timeline
                  items={detail.steps.map((step) => ({
                    children: (
                      <div>
                        <Typography.Text strong>{formatDuration(step.startSecond)} · {step.title}</Typography.Text>
                        <div>虚拟学生：{step.studentPrompt}</div>
                        {step.expectedBehavior && (
                          <Typography.Text type="secondary">观察重点：{step.expectedBehavior}</Typography.Text>
                        )}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本版本为自由授课，仅提供现有课件，不含预设音频或脚本时间线" />
              )}
            </Card>
            <Card title="审核参考" size="small">
              {detail.evaluationCriteria.map(criterion => <Typography.Paragraph key={criterion}>{criterion}</Typography.Paragraph>)}
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title={reviewAction === 'approve' ? '通过 Demo 课堂审核' : '最终拒绝 Demo 课堂'}
        open={Boolean(reviewAction)}
        onCancel={() => setReviewAction(undefined)}
        onOk={submitReview}
        confirmLoading={submitting}
        okText={reviewAction === 'approve' ? '确认通过并启用老师' : '确认最终拒绝'}
        cancelText="取消"
        okButtonProps={{ danger: reviewAction === 'reject' }}
      >
        <Typography.Paragraph>
          {reviewAction === 'approve'
            ? '通过后老师进入上岗准备阶段，完成三项上岗任务后自动开放接课。'
            : '拒绝后老师不能重新参加 Demo，本操作不可撤销。'}
        </Typography.Paragraph>
        <TextArea
          rows={4}
          value={reviewNotes}
          onChange={(event) => setReviewNotes(event.target.value)}
          placeholder={reviewAction === 'approve' ? '审核备注（可选）' : '请填写最终拒绝原因（必填）'}
        />
      </Modal>
    </>
  )
}

export default DemoReviewPanel
